import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ERROR } from 'types';
import { RecommendedCreatorsFilters, RecommendedCreatorsSlice } from 'types/creator-discovery';
import { fetchRecommendedCreatorsThunk } from './thunk';

export const defaultRecommendedCreatorsFilters: RecommendedCreatorsFilters = {
    page: 1,
    sort: 'rank',
};

const initialState: RecommendedCreatorsSlice = {
    error: null,
    loading: false,
    campaigns: {},
    applied_filter_id: null,
    filters: defaultRecommendedCreatorsFilters,
};

const campaignRecommendedCreatorsSlice = createSlice({
    name: 'campaignRecommendedCreators',
    initialState,
    reducers: {
        updateRecommendedCreatorsFilters: (
            state,
            { payload }: PayloadAction<RecommendedCreatorsFilters>
        ) => {
            state.filters = payload;
        },
        clearRecommendedCreatorsCache: (
            state,
            { payload }: PayloadAction<{ campaignId: string }>
        ) => {
            const { campaignId } = payload;
            if (state.campaigns[campaignId]) {
                state.campaigns[campaignId] = { filtered_lists: {} };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRecommendedCreatorsThunk.pending, (state) => {
                state.error = null;
                state.loading = true;
            })
            .addCase(fetchRecommendedCreatorsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                const { filterId, campaignId, filters, data } = action.payload;
                const existingList =
                    state.campaigns?.[campaignId]?.filtered_lists?.[filterId]?.list ?? [];
                const finalList = filters.page === 1 ? data.items : [...existingList, ...data.items];

                state.campaigns[campaignId] = {
                    ...state.campaigns[campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list: finalList,
                            page: filters.page,
                            last_fetched_at: Date.now(),
                            has_more: data.count > finalList.length,
                            filters,
                            count: data.count,
                        },
                    },
                };
                state.applied_filter_id = filterId;
            })
            .addCase(fetchRecommendedCreatorsThunk.rejected, (state, { payload }) => {
                state.loading = false;
                if (payload !== ERROR.CACHED) {
                    state.error = payload || 'Failed to fetch recommended creators';
                }
            });
    },
});

export const {
    updateRecommendedCreatorsFilters,
    clearRecommendedCreatorsCache,
} = campaignRecommendedCreatorsSlice.actions;

export default campaignRecommendedCreatorsSlice.reducer;
